const GST_MULTIPLIER = 1.1;

type GstMode = 'exclusive' | 'inclusive';

interface AssistantAmountOptions {
    displayIsGstInclusive?: boolean;
    actionAmountMode?: unknown;
    autoRepairExclusiveLeak?: boolean;
}

interface DisplayCounterpart {
    display: number;
    displayLabel: 'inc GST' | 'ex GST';
    counterpart: number | null;
    counterpartLabel: 'inc GST' | 'ex GST';
}

export function roundCents(value: number): number | null {
    if (!Number.isFinite(value)) return null;
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function parseMoneyValue(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const match = String(value || '').replace(/,/g, '').match(/[-+]?\d+(?:\.\d+)?/);
    if (!match) return null;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeGstMode(value: unknown): GstMode | null {
    const text = String(value || '').toLowerCase();
    if (/\b(ex|excl|exclusive)\b/.test(text)) return 'exclusive';
    if (/\b(inc|incl|inclusive)\b/.test(text)) return 'inclusive';
    return null;
}

export function getOperationGstMode(operation: unknown): GstMode | null {
    if (!/^set\b/i.test(String(operation || '').trim())) return null;
    return normalizeGstMode(operation);
}

export function formatMoney(value: unknown): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return '$0.00';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function isLikelyExclusiveValueForInclusiveTarget(value: number): boolean {
    if (!Number.isFinite(value) || value <= 0) return false;
    if (Math.abs(value - Math.round(value)) < 0.005) return false;
    const inclusive = roundCents(value * GST_MULTIPLIER);
    if (!Number.isFinite(inclusive)) return false;
    const roundedDollar = Math.round(inclusive!);
    if (Math.abs(inclusive! - roundedDollar) > 0.02) return false;
    return roundedDollar % 10 === 0;
}

export function applyAssistantAmountOperation(
    currentAmount: unknown,
    operation: unknown,
    options: AssistantAmountOptions = {},
): number | null {
    const current = parseMoneyValue(currentAmount) ?? 0;
    const op = String(operation || '').trim();
    if (!op) return null;

    const lower = op.toLowerCase();
    if (lower === 'round') return roundCents(Math.round(current / 100) * 100);
    if (lower === 'round500') return roundCents(Math.round(current / 500) * 500);

    if (/^set\b/i.test(op)) {
        const rawTarget = parseMoneyValue(op.replace(/^set\b/i, ''));
        if (!Number.isFinite(rawTarget)) return null;

        const displayIsGstInclusive = options.displayIsGstInclusive !== false;
        const explicitMode = normalizeGstMode(options.actionAmountMode) || getOperationGstMode(op);
        let target = rawTarget!;

        if (explicitMode === 'inclusive' && !displayIsGstInclusive) {
            target = rawTarget! / GST_MULTIPLIER;
        } else if (explicitMode === 'exclusive' && displayIsGstInclusive) {
            target = rawTarget! * GST_MULTIPLIER;
        } else if (
            !explicitMode &&
            displayIsGstInclusive &&
            options.autoRepairExclusiveLeak &&
            isLikelyExclusiveValueForInclusiveTarget(rawTarget!)
        ) {
            target = rawTarget! * GST_MULTIPLIER;
        }

        return roundCents(Math.max(0, target));
    }

    if (op.includes('%')) {
        const pct = parseMoneyValue(op);
        return Number.isFinite(pct) ? roundCents(current * (1 + pct! / 100)) : null;
    }

    const delta = parseMoneyValue(op);
    return Number.isFinite(delta) ? roundCents(current + delta!) : null;
}

export function describeDisplayAndCounterpart(value: unknown, displayIsGstInclusive: boolean): DisplayCounterpart | null {
    if (value === null || value === undefined) return null;
    const display = Number(value);
    if (!Number.isFinite(display)) return null;
    const counterpart = displayIsGstInclusive
        ? roundCents(display / GST_MULTIPLIER)
        : roundCents(display * GST_MULTIPLIER);
    return {
        display,
        displayLabel: displayIsGstInclusive ? 'inc GST' : 'ex GST',
        counterpart,
        counterpartLabel: displayIsGstInclusive ? 'ex GST' : 'inc GST',
    };
}
